import React, { useState, useEffect, useRef } from "react";
import { 
  Plus, 
  Search, 
  MoreHorizontal,
  Pencil,
  Trash2,
  Users,
  Upload,
  FileText,
  Download,
  Loader2,
  Phone,
  Mail,
  Calendar,
  User,
  BadgeCheck,
  Stethoscope,
  Syringe,
  GraduationCap,
  FileQuestion,
  MapPin,
  FileDown,
  FileBarChart
} from "lucide-react";
import { Layout } from "../components/Layout";
import { RelatorioFilterModal } from "../components/RelatorioFilterModal";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { apiRequest, formatDate } from "../lib/utils";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "../components/ToastProvider";
import { AdminPasswordDialog } from "../components/AdminPasswordDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_DOCUMENTO = [
  { value: "exame_admissional", label: "Exame Admissional", icon: Stethoscope, color: "text-blue-600 bg-blue-50" },
  { value: "exame_periodico", label: "Exame Periodico", icon: Stethoscope, color: "text-teal-600 bg-teal-50" },
  { value: "atestado_medico", label: "Atestado Medico", icon: FileText, color: "text-amber-600 bg-amber-50" },
  { value: "carteira_vacinacao", label: "Carteira de Vacinacao", icon: Syringe, color: "text-emerald-600 bg-emerald-50" },
  { value: "certificado_treinamento", label: "Certificado de Treinamento", icon: GraduationCap, color: "text-purple-600 bg-purple-50" },
  { value: "outro", label: "Outro Documento", icon: FileQuestion, color: "text-slate-600 bg-slate-50" },
];

const initialFormData = {
  nome: "",
  cargo: "",
  data_admissao: "",
  cpf: "",
  telefone: "",
  email: "",
  cep: "",
  logradouro: "",
  número: "",
  complemento: "",
  bairro: "",
  cidade: "",
  estado: "",
  ativo: true,
};

export const Funcionários = () => {
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFunc, setEditingFunc] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteFuncName, setDeleteFuncName] = useState("");
  const [adminPasswordOpen, setAdminPasswordOpen] = useState(false);

  // Upload de documentos
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [selectedFunc, setSelectedFunc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docTipo, setDocTipo] = useState("exame_admissional");
  const [docNome, setDocNome] = useState("");
  const [docValidade, setDocValidade] = useState("");
  const [docObservacoes, setDocObservacoes] = useState("");
  const [deleteDocId, setDeleteDocId] = useState(null);
  const [deleteDocName, setDeleteDocName] = useState("");
  const [adminPasswordDocOpen, setAdminPasswordDocOpen] = useState(false);
  const fileInputRef = useRef(null);
  
  // CEP
  const [loadingCep, setLoadingCep] = useState(false);
  
  // PDF
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [relatorioModalOpen, setRelatorioModalOpen] = useState(false);

  useEffect(() => {
    fetchFuncionarios();
  }, []);

  const fetchFuncionarios = async () => {
    try {
      const data = await apiRequest("/funcionarios");
      setFuncionarios(data);
    } catch (error) {
      console.error("Erro ao carregar funcionarios:", error);
      toast.error("Erro ao carregar funcionarios");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (func = null) => {
    if (func) {
      setEditingFunc(func);
      setFormData({
        nome: func.nome,
        cargo: func.cargo,
        data_admissao: func.data_admissao || "",
        cpf: func.cpf || "",
        telefone: func.telefone || "",
        email: func.email || "",
        cep: func.cep || "",
        logradouro: func.logradouro || "",
        número: func.número || "",
        complemento: func.complemento || "",
        bairro: func.bairro || "",
        cidade: func.cidade || "",
        estado: func.estado || "",
        ativo: func.ativo,
      });
    } else {
      setEditingFunc(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFunc(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingFunc) {
        await apiRequest(`/funcionarios/${editingFunc.id}`, {
          method: "PUT",
          body: JSON.stringify(formData),
        });
        toast.success("Funcionário atualizado com sucesso!");
      } else {
        await apiRequest("/funcionarios", {
          method: "POST",
          body: JSON.stringify(formData),
        });
        toast.success("Funcionário cadastrado com sucesso!");
      }
      handleCloseDialog();
      fetchFuncionarios();
    } catch (error) {
      toast.error(editingFunc ? "Erro ao atualizar funcionário" : "Erro ao cadastrar funcionário");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await apiRequest(`/funcionarios/${deleteId}`, { method: "DELETE" });
      toast.success("Funcionário excluido com sucesso!");
      setDeleteId(null);
      setDeleteFuncName("");
      fetchFuncionarios();
    } catch (error) {
      toast.error("Erro ao excluir funcionário");
    }
  };

  const confirmDelete = (func) => {
    setDeleteId(func.id);
    setDeleteFuncName(func.nome);
    setAdminPasswordOpen(true);
  };

  // Busca de endereco por CEP (ViaCEP)
  const handleCepSearch = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;
    
    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error("CEP não encontrado");
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        cep: cleanCep,
        logradouro: data.logradouro || "",
        bairro: data.bairro || "",
        cidade: data.localidade || "",
        estado: data.uf || "",
      }));
      toast.success("Endereco encontrado!");
    } catch (error) {
      toast.error("Erro ao buscar CEP");
    } finally {
      setLoadingCep(false);
    }
  };

  // Download PDF do funcionário
  const handleDownloadPdf = async (funcId, funcNome) => {
    setDownloadingPdf(funcId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pdf/funcionário/${funcId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erro ao gerar PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ficha_${funcNome.replace(/\s+/g, '_').toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("PDF gerado com sucesso!");
    } catch (error) {
      toast.error("Erro ao gerar PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Funções para documentos
  const openDocDialog = (func) => {
    setSelectedFunc(func);
    setDocTipo("exame_admissional");
    setDocNome("");
    setDocValidade("");
    setDocObservacoes("");
    setDocDialogOpen(true);
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(ext)) {
      toast.error("Apenas arquivos PDF ou imagens são permitidos");
      return;
    }

    if (!docNome.trim()) {
      toast.error("Informe o nome do documento");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', docTipo);
      formData.append('nome_documento', docNome);
      if (docValidade) {
        formData.append('validade', docValidade);
      }
      if (docObservacoes) {
        formData.append('observacoes', docObservacoes);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/funcionarios/${selectedFunc.id}/documento`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Erro ao fazer upload');
      }

      toast.success("Documento enviado com sucesso!");
      setDocNome("");
      setDocValidade("");
      setDocObservacoes("");
      fetchFuncionarios();
      
      // Atualizar o funcionário selecionado
      const updatedFuncs = await apiRequest("/funcionarios");
      const updatedFunc = updatedFuncs.find(f => f.id === selectedFunc.id);
      setSelectedFunc(updatedFunc);
      
    } catch (error) {
      toast.error("Erro ao enviar documento");
      console.error(error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadDoc = async (funcId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/funcionarios/${funcId}/documento/${filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erro ao baixar');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      toast.error("Erro ao baixar documento");
    }
  };

  const handleDeleteDoc = async () => {
    if (!deleteDocId || !selectedFunc) return;
    
    try {
      await apiRequest(`/funcionarios/${selectedFunc.id}/documento/${deleteDocId}`, {
        method: 'DELETE'
      });
      toast.success("Documento excluido!");
      setDeleteDocId(null);
      setDeleteDocName("");
      fetchFuncionarios();
      
      // Atualizar o funcionário selecionado
      const updatedFuncs = await apiRequest("/funcionarios");
      const updatedFunc = updatedFuncs.find(f => f.id === selectedFunc.id);
      setSelectedFunc(updatedFunc);
    } catch (error) {
      toast.error("Erro ao excluir documento");
    }
  };

  const confirmDeleteDoc = (doc) => {
    setDeleteDocId(doc.id);
    setDeleteDocName(doc.nome || doc.tipo);
    setAdminPasswordDocOpen(true);
  };

  const filteredFuncionarios = funcionarios.filter((func) => {
    return func.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
           func.cargo.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Verifica se documento esta vencido ou vencendo
  const getDocStatus = (validade) => {
    if (!validade) return null;
    const hoje = new Date();
    const venc = new Date(validade);
    const diff = Math.ceil((venc - hoje) / (1000 * 60 * 60 * 24));
    if (diff < 0) return "vencido";
    if (diff <= 30) return "vencendo";
    return "ok";
  };

  const getTipoDocConfig = (tipo) => {
    return TIPOS_DOCUMENTO.find(t => t.value === tipo) || TIPOS_DOCUMENTO[5];
  };

  return (
    <Layout>
      <div className="space-y-6 animate-slide-in" data-testid="funcionarios-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Funcionários
            </h1>
            <p className="text-slate-500 mt-1">Aqui serão cadastrados exames dos funcionários, como o admissional, periódico, atestados e treinamentos.</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setRelatorioModalOpen(true)}
              data-testid="relatorio-func-btn"
            >
              <FileBarChart className="w-4 h-4 mr-2" />
              Relatório Geral
            </Button>
            <Button 
              className="bg-teal-600 hover:bg-teal-700" 
              onClick={() => handleOpenDialog()}
              data-testid="new-func-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Funcionário
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nome ou cargo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredFuncionarios.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">Nenhum funcionário encontrado</h3>
              <p className="text-slate-500 mt-1">
                {searchTerm ? "Tente ajustar a busca" : "Comece cadastrando seu primeiro funcionário"}
              </p>
              {!searchTerm && (
                <Button 
                  className="mt-4 bg-teal-600 hover:bg-teal-700"
                  onClick={() => handleOpenDialog()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Cadastrar funcionário
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFuncionarios.map((func) => {
              const docCount = func.documentos?.length || 0;
              
              return (
                <Card 
                  key={func.id} 
                  className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
                  data-testid={`func-card-${func.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 rounded-xl bg-indigo-50 text-indigo-600">
                        <User className="w-5 h-5" />
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`func-actions-${func.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenDialog(func)} className="cursor-pointer">
                            <Pencil className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openDocDialog(func)} className="cursor-pointer">
                            <FileText className="w-4 h-4 mr-2" />
                            Documentos
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDownloadPdf(func.id, func.nome)} 
                            className="cursor-pointer"
                            disabled={downloadingPdf === func.id}
                          >
                            {downloadingPdf === func.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <FileDown className="w-4 h-4 mr-2" />
                            )}
                            Gerar PDF
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => confirmDelete(func)}
                            className="text-red-600 cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <h3 className="font-semibold text-slate-900 mb-1">{func.nome}</h3>
                    <p className="text-sm text-slate-500 mb-3">{func.cargo}</p>
                    
                    <div className="space-y-1 text-sm text-slate-500 mb-3">
                      {func.telefone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3" />
                          <span>{func.telefone}</span>
                        </div>
                      )}
                      {func.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{func.email}</span>
                        </div>
                      )}
                      {func.data_admissao && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3 h-3" />
                          <span>Admissao: {formatDate(func.data_admissao)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {!func.ativo && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                          Inativo
                        </span>
                      )}
                    </div>

                    {/* Badge de documentos */}
                    <div className="pt-4 border-t">
                      <button 
                        onClick={() => openDocDialog(func)}
                        className="w-full flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-600">Documentos</span>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          docCount > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-500'
                        }`}>
                          {docCount}
                        </span>
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>
                {editingFunc ? "Editar Funcionário" : "Novo Funcionário"}
              </DialogTitle>
              <DialogDescription>
                {editingFunc ? "Atualize as informações do funcionário" : "Preencha os dados do novo funcionário"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="nome">Nome Completo *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Nome do funcionário"
                    required
                    data-testid="input-nome"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cargo">Cargo *</Label>
                  <Input
                    id="cargo"
                    value={formData.cargo}
                    onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                    placeholder="Ex: Farmaceutico"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data_admissao">Data de Admissao</Label>
                  <Input
                    id="data_admissao"
                    type="date"
                    value={formData.data_admissao}
                    onChange={(e) => setFormData({ ...formData, data_admissao: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@exemplo.com"
                  />
                </div>

                {/* Seção de Endereco com Busca por CEP */}
                <div className="col-span-2 pt-2 border-t">
                  <Label className="flex items-center gap-2 text-slate-700 mb-3">
                    <MapPin className="w-4 h-4" />
                    Endereco
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="cep">CEP</Label>
                      <div className="flex gap-2">
                        <Input
                          id="cep"
                          value={formData.cep}
                          onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                          onBlur={(e) => handleCepSearch(e.target.value)}
                          placeholder="00000-000"
                          maxLength={9}
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="icon"
                          onClick={() => handleCepSearch(formData.cep)}
                          disabled={loadingCep}
                          title="Buscar CEP"
                        >
                          {loadingCep ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="estado">Estado</Label>
                      <Input
                        id="estado"
                        value={formData.estado}
                        onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                        placeholder="UF"
                        maxLength={2}
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="logradouro">Logradouro</Label>
                      <Input
                        id="logradouro"
                        value={formData.logradouro}
                        onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                        placeholder="Rua, Avenida, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="número">Número</Label>
                      <Input
                        id="número"
                        value={formData.número}
                        onChange={(e) => setFormData({ ...formData, número: e.target.value })}
                        placeholder="Nº"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="complemento">Complemento</Label>
                      <Input
                        id="complemento"
                        value={formData.complemento}
                        onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                        placeholder="Apto, Sala, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bairro">Bairro</Label>
                      <Input
                        id="bairro"
                        value={formData.bairro}
                        onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                        placeholder="Bairro"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cidade">Cidade</Label>
                      <Input
                        id="cidade"
                        value={formData.cidade}
                        onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                        placeholder="Cidade"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="ativo"
                  checked={formData.ativo}
                  onCheckedChange={(checked) => setFormData({ ...formData, ativo: checked })}
                />
                <Label htmlFor="ativo">Funcionário ativo</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={saving} data-testid="submit-btn">
                  {saving ? "Salvando..." : editingFunc ? "Salvar" : "Cadastrar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Documentos Dialog */}
        <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  Documentos do Funcionário
                </div>
              </DialogTitle>
              <DialogDescription>
                {selectedFunc?.nome} - Gerencie exames, atestados e certificados
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Formulário de upload */}
              <Card className="border-dashed border-2">
                <CardContent className="p-4">
                  <h4 className="font-medium text-slate-900 mb-3">Adicionar novo documento</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 sm:col-span-1">
                      <Label>Tipo de documento *</Label>
                      <Select value={docTipo} onValueChange={setDocTipo}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOS_DOCUMENTO.map((tipo) => (
                            <SelectItem key={tipo.value} value={tipo.value}>
                              {tipo.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <Label>Validade (opcional)</Label>
                      <Input
                        type="date"
                        value={docValidade}
                        onChange={(e) => setDocValidade(e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Nome do documento *</Label>
                      <Input
                        value={docNome}
                        onChange={(e) => setDocNome(e.target.value)}
                        placeholder="Ex: ASO Admissional - Janeiro 2026"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Observacoes (opcional)</Label>
                      <Textarea
                        value={docObservacoes}
                        onChange={(e) => setDocObservacoes(e.target.value)}
                        placeholder="Observacoes adicionais..."
                        rows={2}
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        ref={fileInputRef}
                        onChange={handleUploadDoc}
                        className="hidden"
                        id="docFile"
                      />
                      <Button 
                        type="button"
                        onClick={() => {
                          if (!docNome.trim()) {
                            toast.error("Informe o nome do documento primeiro");
                            return;
                          }
                          fileInputRef.current?.click();
                        }}
                        disabled={uploading}
                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Selecionar arquivo (PDF ou Imagem)
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Lista de documentos */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">
                  Documentos anexados ({selectedFunc?.documentos?.length || 0})
                </h4>
                
                {(!selectedFunc?.documentos || selectedFunc.documentos.length === 0) ? (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">Nenhum documento anexado</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedFunc.documentos.map((doc) => {
                      const status = getDocStatus(doc.validade);
                      const tipoConfig = getTipoDocConfig(doc.tipo);
                      const IconComponent = tipoConfig.icon;
                      
                      return (
                        <div 
                          key={doc.id} 
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            status === 'vencido' ? 'bg-red-50 border-red-200' :
                            status === 'vencendo' ? 'bg-amber-50 border-amber-200' :
                            'bg-white border-slate-200'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`p-2 rounded-lg ${tipoConfig.color}`}>
                              <IconComponent className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900 truncate">{doc.nome}</p>
                              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                <span className="px-1.5 py-0.5 rounded bg-slate-100">{tipoConfig.label}</span>
                                <span>Enviado em {formatDate(doc.data_upload)}</span>
                                {doc.validade && (
                                  <span className={`px-1.5 py-0.5 rounded ${
                                    status === 'vencido' ? 'bg-red-100 text-red-700' :
                                    status === 'vencendo' ? 'bg-amber-100 text-amber-700' :
                                    'bg-emerald-100 text-emerald-700'
                                  }`}>
                                    {status === 'vencido' ? 'Vencido' : 
                                     status === 'vencendo' ? 'Vencendo' : 
                                     `Válido até ${formatDate(doc.validade)}`}
                                  </span>
                                )}
                              </div>
                              {doc.observacoes && (
                                <p className="text-xs text-slate-400 mt-1 truncate">{doc.observacoes}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={() => handleDownloadDoc(selectedFunc.id, doc.arquivo)}
                              title="Baixar documento"
                            >
                              <Download className="w-4 h-4 text-indigo-600" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={() => confirmDeleteDoc(doc)}
                              title="Excluir documento"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDocDialogOpen(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Admin Password Dialog para Excluir Funcionário */}
        <AdminPasswordDialog
          open={adminPasswordOpen}
          onOpenChange={(open) => {
            setAdminPasswordOpen(open);
            if (!open) {
              setDeleteId(null);
              setDeleteFuncName("");
            }
          }}
          onConfirm={handleDelete}
          title="Excluir Funcionário"
          description="Todos os documentos anexados também serão excluídos. Esta ação não pode ser desfeita."
          itemName={deleteFuncName}
        />

        {/* Admin Password Dialog para Excluir Documento */}
        <AdminPasswordDialog
          open={adminPasswordDocOpen}
          onOpenChange={(open) => {
            setAdminPasswordDocOpen(open);
            if (!open) {
              setDeleteDocId(null);
              setDeleteDocName("");
            }
          }}
          onConfirm={handleDeleteDoc}
          title="Excluir Documento"
          description="O arquivo será removido permanentemente."
          itemName={deleteDocName}
        />

        {/* Modal de Filtros para Relatório Geral */}
        <RelatorioFilterModal
          open={relatorioModalOpen}
          onOpenChange={setRelatorioModalOpen}
          title="Relatório Geral de Funcionários"
          description="Selecione os filtros para gerar o relatório em PDF"
          endpoint="/pdf/funcionarios"
          filters={[
            {
              key: "cargo",
              label: "Cargo",
              type: "text",
              placeholder: "Filtrar por cargo..."
            },
            {
              key: "situacao",
              label: "Situação",
              type: "select",
              options: [
                { value: "ativo", label: "Ativos" },
                { value: "inativo", label: "Inativos" }
              ]
            },
            {
              key: "com_pendencias",
              label: "Documentos",
              type: "select",
              options: [
                { value: "true", label: "Com pendências" },
                { value: "false", label: "Sem pendências" }
              ]
            },
            {
              key: "incluir_documentos",
              label: "Incluir lista de documentos",
              type: "select",
              options: [
                { value: "true", label: "Sim" },
                { value: "false", label: "Não" }
              ]
            }
          ]}
        />
      </div>
    </Layout>
  );
};

export default Funcionários;
